import { createFilterStore } from 'shared/state/create-filter-store';
import type { Animal, BreedType, Characteristic } from 'shared/@types';
import {
  Animal as AnimalValues,
  BreedType as BreedTypeValues,
  Characteristic as CharacteristicValues,
} from 'shared/@generated/models';
import { sanitizeQueryParams } from 'shared/library/api-client';

export type BreedsFilter = {
  query: string;
  animal: Animal | null;
  breedType: BreedType | null;
  size: string | null;
  country: string | null;
  characteristics: Characteristic[] | null;
  page: number;
  sortBy: string | null;
};

export type LockedBreedsFilter = Partial<Pick<BreedsFilter, 'animal' | 'size'>>;

export const defaultBreedsFilter: BreedsFilter = {
  query: '',
  animal: null,
  breedType: null,
  size: null,
  country: null,
  characteristics: null,
  page: 1,
  sortBy: null,
};

export const sortOptions = ['name'];

function isEnumValue<T extends string>(values: Record<string, T>, value: unknown): value is T {
  return typeof value === 'string' && Object.values(values).includes(value as T);
}

export function parseBreedsFilter(values: Partial<BreedsFilter>): BreedsFilter {
  const parsed = sanitizeQueryParams(values, defaultBreedsFilter, ['characteristics']);
  const characteristics = Array.isArray(parsed.characteristics)
    ? parsed.characteristics.filter((value) => isEnumValue(CharacteristicValues, value))
    : null;

  return {
    query: typeof parsed.query === 'string' ? parsed.query : '',
    animal: isEnumValue(AnimalValues, parsed.animal) ? parsed.animal : null,
    breedType: isEnumValue(BreedTypeValues, parsed.breedType) ? parsed.breedType : null,
    size:
      isEnumValue(CharacteristicValues, parsed.size) && parsed.size.startsWith('size_')
        ? parsed.size
        : null,
    country: typeof parsed.country === 'string' ? parsed.country : null,
    characteristics: characteristics?.length ? characteristics : null,
    page:
      typeof parsed.page === 'number' && Number.isInteger(parsed.page) && parsed.page > 0
        ? parsed.page
        : 1,
    sortBy:
      typeof parsed.sortBy === 'string' && sortOptions.includes(parsed.sortBy)
        ? parsed.sortBy
        : null,
  };
}

export function getBreedsRouteFilter(
  filter: BreedsFilter,
  lockedFilter?: LockedBreedsFilter,
): BreedsFilter {
  return {
    ...filter,
    ...(lockedFilter?.animal !== undefined ? { animal: null } : {}),
    ...(lockedFilter?.size !== undefined ? { size: null } : {}),
  };
}

export function shouldNoIndexBreedsFilter(
  filter: BreedsFilter,
  ignoredKeys: (keyof BreedsFilter)[] = [],
) {
  return (Object.keys(defaultBreedsFilter) as (keyof BreedsFilter)[]).some((key) => {
    if (key === 'page' || ignoredKeys.includes(key)) {
      return false;
    }

    const value = filter[key];
    return Array.isArray(value)
      ? value.length > 0
      : value !== null && value !== undefined && value !== '';
  });
}

export const [BreedsFilterProvider, useBreedsFilterStore] = createFilterStore(defaultBreedsFilter);
